import { useState, ReactNode, useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import { useTranslation } from 'react-i18next'

import PdfViewer from 'src/components/shared/PdfViewer'
import DialogActions from 'src/components/shared/ui/DialogActions'
import DialogContent from 'src/components/shared/ui/DialogContent'
import DialogTitle from 'src/components/shared/ui/DialogTitle/DialogTitle'

import { DownloadIcon } from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { theme } from 'src/providers/ThemeProvider'
import ButtonPrimaryOutlined from '../ui/ButtonPrimaryOutlined'
import useStyles from './useStyles'

type Props = {
  actions?: ReactNode[]
  allowDownload?: boolean
  children?: ReactNode | ((open: boolean, openDialog: () => void) => ReactNode) | null
  documentB64?: string | null
  documentUri?: string | null
  filename?: string | null
  loading?: boolean
  open?: boolean
  onClose?: () => void
  onPdfError?: () => void
}

export default function PdfDialog({
  actions = [],
  allowDownload = true,
  children,
  documentB64 = null,
  documentUri,
  filename,
  loading = false,
  open = false,
  onClose,
  onPdfError
}: Props): JSX.Element {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(open)
  const { t } = useTranslation()

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const openDialog = () => setIsOpen(true)

  function handleClose() {
    if (onClose) {
      onClose()
    } else {
      setIsOpen(false)
    }
  }

  function saveFile(url: string) {
    const a = document.createElement('a')
    a.href = url
    a.download = filename ?? 'file'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  async function onDownload() {
    if (documentUri) {
      // window.open(documentUri, '_blank')
      saveFile(documentUri)
    }
  }

  const documentFile = documentB64 || documentUri || null

  const icons =
    allowDownload && documentFile
      ? [
          <ButtonPrimaryOutlined onClick={onDownload} variant="text">
            <DownloadIcon
              htmlColor={theme.corporate.primaryColor}
              sx={{ fontSize: '22px', marginRight: '4px' }}
            />
            {t('actions.download')}
          </ButtonPrimaryOutlined>
        ]
      : []

  const loader = (
    <div className={classes.loader}>
      <Loader />
    </div>
  )

  return (
    <>
      {typeof children === 'function' ? children(isOpen, openDialog) : children}
      <Dialog
        classes={{ paper: classes.paper }}
        onClose={handleClose}
        open={isOpen}
        fullWidth
        maxWidth="md"
        fullScreen={false}
      >
        <DialogTitle
          id="pdf-dialog"
          // onClose={handleClose}
          className={classes.dialogTitle}
        >
          {filename ?? ''}
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <div className={classes.pdfViewer}>
            {loading || !documentFile ? (
              loader
            ) : (
              <PdfViewer document={documentFile} loader={loader} onError={onPdfError} />
            )}
          </div>
        </DialogContent>
        {actions.length > 0 || icons?.length > 0 ? (
          <DialogActions>
            {icons}
            {actions}
          </DialogActions>
        ) : null}
      </Dialog>
    </>
  )
}
